import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { AlertTriangle, Boxes, Edit, Search } from "lucide-react";
import blackTshirt from "../../../images/shop/black-tshirt.png";
import chainBag from "../../../images/shop/chain-bag.png";
import sneakers from "../../../images/shop/sneakers.png";
import backpack from "../../../images/shop/backpack.png";

const Page = styled.main`
  color: #172033;
`;

const Header = styled.section`
  margin-bottom: 18px;
  padding: 24px 26px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);

  h1 {
    margin: 0 0 8px;
    font-size: 30px;
  }

  p {
    margin: 0;
    color: #66758f;
  }
`;

const Stats = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 16px;
`;

const Stat = styled.article`
  padding: 18px;
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;

  svg {
    color: ${(props) => (props.$danger ? "#ef4444" : "#2f80ed")};
  }

  span {
    display: block;
    margin-top: 10px;
    color: #66758f;
    font-size: 13px;
    font-weight: 800;
  }

  strong {
    display: block;
    margin-top: 5px;
    font-size: 24px;
  }
`;

const Toolbar = styled.section`
  display: grid;
  grid-template-columns: 1fr 150px;
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: #fff;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  height: 42px;
  padding: 0 13px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  color: #8da0b8;

  input {
    width: 100%;
    border: 0;
    outline: 0;
  }
`;

const Select = styled.select`
  height: 42px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  background: #fff;
  color: #35445c;
  padding: 0 12px;
  font-weight: 800;
`;

const Table = styled.section`
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 110px 130px 90px;
  gap: 14px;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #edf2f8;

  &:last-child {
    border-bottom: 0;
  }

  &.head {
    background: #f7fbff;
    color: #66758f;
    font-size: 12px;
    font-weight: 900;
  }
`;

const Product = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    width: 54px;
    height: 54px;
    object-fit: contain;
    border: 1px solid #dfe8f4;
    border-radius: 8px;
  }

  strong {
    display: block;
  }

  span {
    color: #66758f;
    font-size: 12px;
  }
`;

const Stock = styled.span`
  justify-self: start;
  border-radius: 999px;
  padding: 7px 10px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$fg};
  font-size: 12px;
  font-weight: 900;
`;

const IconButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  background: #fff;
  color: #66758f;
  cursor: pointer;
`;

const inventory = [
  { id: 1, sku: "AN-TS-001", name: "로고 크루넥 코튼 티셔츠", category: "의류", stock: 25, safety: 8, image: blackTshirt },
  { id: 2, sku: "AN-BG-002", name: "GG 마틀라세 숄더백", category: "가방", stock: 8, safety: 6, image: chainBag },
  { id: 3, sku: "AN-SH-003", name: "에어 조던 1 로우", category: "신발", stock: 43, safety: 10, image: sneakers },
  { id: 4, sku: "AN-BP-006", name: "체크 백팩", category: "가방", stock: 0, safety: 5, image: backpack },
];

function getStockInfo(item) {
  if (item.stock === 0) return { label: "품절", bg: "#fee2e2", fg: "#dc2626" };
  if (item.stock <= item.safety) return { label: "부족", bg: "#fff7ed", fg: "#ea580c" };
  return { label: "정상", bg: "#ecfdf5", fg: "#059669" };
}

export default function AdminInventory() {
  const [keyword, setKeyword] = useState("");
  const [state, setState] = useState("전체");

  const filteredInventory = useMemo(() => {
    return inventory.filter((item) => {
      const info = getStockInfo(item);
      const target = `${item.name} ${item.sku} ${item.category}`.toLowerCase();
      return (!keyword || target.includes(keyword.toLowerCase())) && (state === "전체" || info.label === state);
    });
  }, [keyword, state]);

  const totalStock = inventory.reduce((sum, item) => sum + item.stock, 0);
  const lowStock = inventory.filter((item) => getStockInfo(item).label === "부족").length;
  const outStock = inventory.filter((item) => getStockInfo(item).label === "품절").length;

  return (
    <Page>
      <Header>
        <h1>재고 관리</h1>
        <p>안전 재고 이하 상품과 품절 상품을 빠르게 확인합니다.</p>
      </Header>

      <Stats>
        <Stat><Boxes size={24} /><span>전체 SKU</span><strong>{inventory.length}</strong></Stat>
        <Stat><Boxes size={24} /><span>총 재고</span><strong>{totalStock}</strong></Stat>
        <Stat $danger><AlertTriangle size={24} /><span>재고 부족</span><strong>{lowStock}</strong></Stat>
        <Stat $danger><AlertTriangle size={24} /><span>품절</span><strong>{outStock}</strong></Stat>
      </Stats>

      <Toolbar>
        <SearchBox><Search size={17} /><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="상품명 또는 SKU 검색" /></SearchBox>
        <Select value={state} onChange={(event) => setState(event.target.value)}>
          <option>전체</option><option>정상</option><option>부족</option><option>품절</option>
        </Select>
      </Toolbar>

      <Table>
        <Row className="head"><div>상품</div><div>카테고리</div><div>재고</div><div>상태</div><div>작업</div></Row>
        {filteredInventory.map((item) => {
          const info = getStockInfo(item);
          return (
            <Row key={item.id}>
              <Product><img src={item.image} alt={item.name} /><div><strong>{item.name}</strong><span>{item.sku}</span></div></Product>
              <div>{item.category}</div>
              <b>{item.stock}개</b>
              <Stock $bg={info.bg} $fg={info.fg}>{info.label}</Stock>
              <IconButton type="button" aria-label="재고 수정"><Edit size={15} /></IconButton>
            </Row>
          );
        })}
      </Table>
    </Page>
  );
}
